import { clsx } from 'clsx';
import { TrendingUp } from 'lucide-react';
import type { GenerationBrief } from '@/services/api';
import type { BriefLaneKpis } from './SwimLane';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(isoStr?: string): string {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

function fmtSpend(n: number): string {
  if (n === 0) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function fmtImpressions(n: number): string {
  if (n === 0) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return `${n}`;
}

const SOURCE_TYPE_LABELS: Record<string, { label: string; icon: string }> = {
  persona_enriched: { label: 'Persona Enriched', icon: '🧠' },
  screenshot_extraction: { label: 'Screenshot Extraction', icon: '📸' },
  freeform_input: { label: 'Freeform Input', icon: '✍️' },
  asset_backfill: { label: 'Asset Backfill', icon: '📦' },
  creative_review: { label: 'Creative Review Backfill', icon: '🔍' },
  competitor_analysis: { label: 'Competitor Analysis', icon: '🎯' },
  template_clone: { label: 'Template Clone', icon: '📋' },
  ad_pipeline: { label: 'Ad Pipeline', icon: '⚡' },
  session_context: { label: 'Session Context', icon: '💬' },
  manual: { label: 'Manual', icon: '✏️' },
};

function getSourceTypeLabel(sourceType?: string): { label: string; icon: string } {
  if (!sourceType) return { label: 'Unknown', icon: '❓' };
  return SOURCE_TYPE_LABELS[sourceType] || { label: sourceType, icon: '📄' };
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  active: 'bg-teal-50 text-teal-700 border-teal-200',
  archived: 'bg-slate-50 text-slate-400 border-slate-200',
  superseded: 'bg-amber-50 text-amber-700 border-amber-200',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={clsx(
        'text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border',
        STATUS_COLORS[status] ?? 'bg-slate-50 text-slate-500 border-slate-200',
      )}
    >
      {status}
    </span>
  );
}

function QualityBar({ value, className }: { value: number; className?: string }) {
  const percent = Math.round(value * 100);
  const color = percent < 50 ? 'bg-rose-400' : percent < 80 ? 'bg-amber-400' : 'bg-emerald-400';
  return (
    <div className={clsx('bg-slate-200 rounded-full overflow-hidden', className ?? 'h-1.5 w-16')}>
      <div className={clsx('h-full rounded-full transition-all', color)} style={{ width: `${percent}%` }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI strip — shown when the card is rendered inside a swim lane with
// per-brief performance data from the /swim-lanes API.
// ---------------------------------------------------------------------------

function RankBadge({ rank }: { rank: number }) {
  const cls =
    rank === 1
      ? 'bg-teal-500 text-white'
      : rank === 2
        ? 'bg-teal-400 text-white'
        : rank <= 5
          ? 'bg-teal-50 text-teal-700 border border-teal-200'
          : 'bg-slate-100 text-slate-500';
  return (
    <span className={clsx('text-[10px] font-bold px-1.5 py-0.5 rounded', cls)}>
      #{rank}
    </span>
  );
}

function KpiStrip({ kpis }: { kpis: BriefLaneKpis }) {
  const hasData = kpis.spend > 0 || kpis.ad_count > 0;

  return (
    <div className="flex items-center gap-2 pt-1 border-t border-slate-100 mt-1">
      <RankBadge rank={kpis.rank} />

      {kpis.ad_count > 0 && (
        <span className="text-[11px] text-slate-500">
          {kpis.ad_count} ad{kpis.ad_count !== 1 ? 's' : ''}
        </span>
      )}

      {hasData && kpis.spend > 0 && (
        <>
          <span className="text-slate-300">·</span>
          <span className="text-[11px] text-slate-500">{fmtSpend(kpis.spend)}</span>
        </>
      )}

      {hasData && kpis.ltv_roas > 0 && (
        <>
          <span className="text-slate-300">·</span>
          <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-teal-700">
            <TrendingUp size={9} />
            {kpis.ltv_roas.toFixed(1)}x
          </span>
        </>
      )}

      {hasData && kpis.impressions > 0 && (
        <>
          <span className="text-slate-300">·</span>
          <span className="text-[11px] text-slate-400">{fmtImpressions(kpis.impressions)} impr</span>
        </>
      )}

      {!hasData && (
        <span className="text-[11px] text-slate-400 italic">No spend data yet</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BriefLibraryCard — compact card render of a single Brief in the swim board.
// ---------------------------------------------------------------------------

export interface BriefLibraryCardProps {
  brief: GenerationBrief;
  /** Performance KPIs from the swim-lanes API. When provided, a KPI strip is rendered. */
  kpis?: BriefLaneKpis;
  onClick: () => void;
}

export function BriefLibraryCard({ brief, kpis, onClick }: BriefLibraryCardProps) {
  const sourceInfo = getSourceTypeLabel(brief.provenance?.source_type);
  const playbookCount = brief.playbook_ids?.length ?? 0;
  const audienceName = brief.audience?.persona_name || brief.persona_id?.replace(/_/g, ' ') || 'Unknown persona';
  const canonicalSlug = brief.persona_id || '';

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white rounded-xl border border-slate-200 p-4 cursor-pointer hover:border-teal-300 hover:shadow-sm transition-all group flex flex-col gap-2 w-full"
    >
      <div className="flex items-center gap-2">
        <StatusBadge status={brief.status} />
        {brief.version > 1 && (
          <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
            v{brief.version}
          </span>
        )}
        {brief.quality?.confidence_score !== undefined && (
          <QualityBar value={brief.quality.confidence_score} className="ml-auto h-1.5 w-12" />
        )}
      </div>

      <div className="font-semibold text-sm text-navy-900 truncate">{brief.name}</div>

      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="truncate">{audienceName}</span>
          <span className="text-slate-300">·</span>
          <span className="font-mono text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded truncate">
            {brief.client_uid}
          </span>
        </div>
        {canonicalSlug && canonicalSlug !== audienceName.toLowerCase().replace(/ /g, '_') && (
          <span className="font-mono text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded self-start">
            {canonicalSlug}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-1">
        <span className="truncate">
          <span className="mr-1">{sourceInfo.icon}</span>
          {sourceInfo.label}
        </span>
        <span className="text-slate-300">·</span>
        <span>{timeAgo(brief.created_at)}</span>
        {playbookCount > 0 && (
          <>
            <span className="text-slate-300 ml-auto">·</span>
            <span className="text-teal-500">
              {playbookCount} ad pack{playbookCount !== 1 ? 's' : ''} →
            </span>
          </>
        )}
      </div>

      {kpis && <KpiStrip kpis={kpis} />}
    </button>
  );
}
